import {
  HistoricalLayersService,
  HistoricalYear,
  HistoricalTileSource,
  HistoricalLayerProvider,
} from '../../types/historical-layers';
import availableHistoricalYearsData from './available-historical-years.json';
import { checkTileHasData } from '../../utilities/map-utils';
import { mcApi } from '../McApi';

/**
 * Implementation of HistoricalLayersService using Google Earth Engine
 *
 * Google Earth Engine provides access to NAIP and other high-resolution imagery
 * with true year-specific historical data at 60cm-1m resolution.
 *
 * SETUP REQUIRED:
 *
 * 1. Create Google Earth Engine Project:
 *    - Go to https://code.earthengine.google.com/
 *    - Sign up for Earth Engine access (free for research/education)
 *    - Create a Cloud Project linked to Earth Engine
 *
 * 2. Create Service Account:
 *    - Go to Google Cloud Console
 *    - Create Service Account with Earth Engine API access
 *    - Download service account key JSON
 *
 * BENEFITS:
 * - True year-specific historical imagery
 * - High resolution (60cm-1m)
 * - Free for research/education use
 * - Access to multiple imagery sources
 * - Covers entire US with NAIP data
 */
export class HistoricalLayersServiceImpl implements HistoricalLayersService {
  private readonly GOOGLE_SAT_URL = 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}';
  private readonly GOOGLE_ATTRIBUTION = '© Google';

  // Google Earth Engine attribution
  private readonly GEE_ATTRIBUTION = 'Google Earth Engine, USDA NAIP';

  // Load years with verified working tile sources from JSON file
  // Map IDs are pre-generated from the backend API.
  // These map IDs are stable and can be reused.
  //
  // To generate new map IDs:
  // 1. Start the backend API: cd src/services/generator-service/historical-layers && yarn start
  // 2. Fetch map ID: yarn generate-map-ids 2024 2023 2022
  // 3. Update available-historical-years.json with the new map IDs
  //
  // NOTE: Map IDs may expire after some time. If imagery stops loading,
  // regenerate map IDs using the steps above.
  private readonly AVAILABLE_HISTORICAL_YEARS: Record<
    number,
    {
      url: string;
      attribution: string;
      provider: string;
      sourceType?: 'xyz' | 'wms';
      maxZoom?: number;
      minZoom?: number;
      year?: number;
    }
  >;

  // Dynamically fetched years from backend (takes priority over pre-configured)
  private dynamicYears: Record<
    number,
    {
      url: string;
      attribution: string;
      provider: string;
      sourceType?: 'xyz' | 'wms';
      maxZoom?: number;
      minZoom?: number;
      year?: number;
      mapId?: string;
      token?: string;
      imageCount?: number;
      dateRange?: string;
      season?: string;
    }
  > = {};

  constructor() {
    // Load historical years data and add attribution
    this.AVAILABLE_HISTORICAL_YEARS = Object.fromEntries(
      Object.entries(availableHistoricalYearsData).map(([year, data]) => {
        const yearData = data as {
          url: string;
          provider: string;
          sourceType?: 'xyz' | 'wms';
          maxZoom?: number;
          minZoom?: number;
          year?: number;
        };

        return [
          parseInt(year),
          {
            ...yearData,
            attribution: this.GEE_ATTRIBUTION,
            sourceType: yearData.sourceType as 'xyz' | 'wms',
          },
        ];
      }),
    ) as Record<
      number,
      {
        url: string;
        attribution: string;
        provider: string;
        sourceType?: 'xyz' | 'wms';
        maxZoom?: number;
        minZoom?: number;
        year?: number;
      }
    >;
  }

  /**
   * Update the service with dynamically fetched years from the backend
   * This method fetches available years for a location and updates the internal state
   *
   * @param lon - Longitude of the location
   * @param lat - Latitude of the location
   * @param bounds - Optional bounding box for the area of interest
   * @param count - Number of recent years to fetch (default: 3)
   * @param cacheId - Optional cache/job ID for organizing cached map IDs
   * @param forceRefresh - Whether to force refresh and ignore cache (default: false)
   */
  async updateAvailableYears(
    lon: number,
    lat: number,
    bounds?: { xmin: number; ymin: number; xmax: number; ymax: number },
    count: number = 3,
    cacheId?: string,
    forceRefresh: boolean = false,
  ): Promise<void> {
    try {
      // Clear previously fetched dynamic years to avoid accumulation
      this.dynamicYears = {};

      // Use McApi to fetch recent years from Earth Engine API
      const result = await mcApi.fetchRecentHistoricalYears(lon, lat, bounds, count, cacheId, forceRefresh);

      if (!result.success || !result.years) {
        throw new Error(result.error || 'Failed to fetch recent years from backend');
      }

      // Update dynamic years with the fetched data
      for (const yearData of result.years) {
        this.dynamicYears[yearData.year] = {
          url: yearData.tileUrl,
          attribution: this.GEE_ATTRIBUTION,
          provider: 'google-earth-engine',
          sourceType: 'xyz',
          year: yearData.year,
          mapId: yearData.mapId,
          token: yearData.token,
        };
      }

      console.log(`✓ Updated ${result.years.length} year(s) from backend`);
    } catch (error) {
      console.error('Error updating available years:', error);
      throw error;
    }
  }

  getAvailableYears(): HistoricalYear[] {
    const currentYear = this.getCurrentYear();
    const years: HistoricalYear[] = [];

    // Always include current year
    years.push({
      year: currentYear,
      displayName: `${currentYear} (Current)`,
      isCurrentYear: true,
    });

    // Merge dynamic years and pre-configured years (dynamic takes priority)
    const allYears = new Set([
      ...Object.keys(this.dynamicYears).map(Number),
      ...Object.keys(this.AVAILABLE_HISTORICAL_YEARS).map(Number),
    ]);

    // Sort years descending and add them
    const historicalYears = Array.from(allYears).sort((a, b) => b - a);

    for (const year of historicalYears) {
      // Check if we have data for this year (either dynamic or pre-configured)
      years.push({
        year,
        displayName: `${year}`,
        isCurrentYear: false,
      });
    }

    return years;
  }

  async getAvailableYearsForLocation(lon: number, lat: number): Promise<HistoricalYear[]> {
    try {
      // Convert lon/lat to tile coordinates at zoom level 18
      const zoom = 18;
      const tileX = Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
      const tileY = Math.floor(
        ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) *
          Math.pow(2, zoom),
      );

      const currentYear = this.getCurrentYear();
      const years: HistoricalYear[] = [];

      // Always include current year
      years.push({
        year: currentYear,
        displayName: `${currentYear} (Current)`,
        isCurrentYear: true,
      });

      // Check each historical year by testing if tiles load
      const yearCheckPromises = Object.keys(this.AVAILABLE_HISTORICAL_YEARS).map((yearStr) => {
        return new Promise<{ year: number; hasData: boolean }>(async (resolve) => {
          const year = parseInt(yearStr);
          const source = this.AVAILABLE_HISTORICAL_YEARS[year];

          // Build tile URL for this location
          const tileUrl = source.url
            .replace('{z}', zoom.toString())
            .replace('{x}', tileX.toString())
            .replace('{y}', tileY.toString());

          // Check if tile has data using utility function
          const hasData = await checkTileHasData(tileUrl, 5000);
          resolve({ year, hasData });
        });
      });

      const results = await Promise.all(yearCheckPromises);

      // Add years that have data at this location
      for (const result of results.sort((a, b) => b.year - a.year)) {
        if (result.hasData) {
          years.push({
            year: result.year,
            displayName: `${result.year}`,
            isCurrentYear: false,
          });
        }
      }

      console.log(`Found ${years.length - 1} historical years with data at lon=${lon}, lat=${lat}`);

      return years;
    } catch (error) {
      console.error('Error checking available years for location:', error);

      // Fall back to returning all years if tile checking fails
      return this.getAvailableYears();
    }
  }

  getTileSourceForYear(year: number): HistoricalTileSource {
    const availableYears = this.getAvailableYears().map((y) => y.year);

    if (!availableYears.includes(year)) {
      throw new Error(`Year ${year} is not available`);
    }

    // Current year uses Google satellite imagery
    if (year === this.getCurrentYear()) {
      return {
        year,
        url: this.GOOGLE_SAT_URL,
        attribution: this.GOOGLE_ATTRIBUTION,
        provider: 'current',
      };
    }

    // Check dynamic years first (they take priority)
    const dynamicSource = this.dynamicYears[year];

    if (dynamicSource) {
      return {
        year,
        url: dynamicSource.url,
        attribution: dynamicSource.attribution,
        provider: dynamicSource.provider as HistoricalLayerProvider,
        sourceType: dynamicSource.sourceType,
        maxZoom: dynamicSource.maxZoom,
        minZoom: dynamicSource.minZoom,
      };
    }

    // Fall back to pre-configured historical tile source
    const historicalSource = this.AVAILABLE_HISTORICAL_YEARS[year];

    if (historicalSource) {
      return {
        year,
        url: historicalSource.url,
        attribution: historicalSource.attribution,
        provider: historicalSource.provider as HistoricalLayerProvider,
        sourceType: historicalSource.sourceType,
        maxZoom: historicalSource.maxZoom,
        minZoom: historicalSource.minZoom,
      };
    }

    // This should never happen if getAvailableYears() is working correctly
    // But provide a fallback just in case
    throw new Error(`No tile source configured for year ${year}`);
  }

  getCurrentYear(): number {
    return new Date().getFullYear();
  }
}
